/**
 * WebMCP tool definitions (webmachinelearning.github.io/webmcp) — the
 * site's key actions exposed to in-browser AI agents via
 * `navigator.modelContext`. Registered on public pages by
 * components/webmcp-provider.tsx.
 *
 * Plain module (no directive) so the definitions + execute callbacks
 * are unit-testable; browser-only globals are touched INSIDE execute,
 * never at module scope. Tools are read-only or pure-navigation — no
 * mutations, no auth actions (agents authenticate via the real OAuth
 * surface documented at /auth.md, not through the browser bridge).
 */

interface WebMcpTextResult {
  content: { type: 'text'; text: string }[];
}

export interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (input: Record<string, unknown>) => Promise<WebMcpTextResult>;
}

function text(t: string): WebMcpTextResult {
  return { content: [{ type: 'text', text: t }] };
}

/** Same-origin path guard for the navigation tool. */
export function isSafeSitePath(path: unknown): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');
}

export function buildWebmcpTools(): WebMcpTool[] {
  return [
    {
      name: 'get_page_markdown',
      description:
        'Fetch the markdown representation of a connect0.ai page (content negotiation — every public page serves markdown). Defaults to the current page.',
      inputSchema: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: "Site-relative path, e.g. '/docs/quickstart'. Defaults to the current page.",
          },
        },
      },
      execute: async (input) => {
        const path = isSafeSitePath(input.path) ? input.path : window.location.pathname;
        const res = await fetch(path, { headers: { Accept: 'text/markdown' } });
        const type = res.headers.get('content-type') ?? '';
        if (!res.ok || !type.includes('text/markdown')) {
          return text(`No markdown variant for ${path} (HTTP ${res.status}, ${type || 'unknown type'}).`);
        }
        return text(await res.text());
      },
    },
    {
      name: 'list_content_catalogue',
      description:
        'List every page, doc, guide, and blog post on connect0.ai with URLs and markdown variants (the llms.txt catalogue).',
      inputSchema: { type: 'object', properties: {} },
      execute: async () => {
        const res = await fetch('/llms.txt');
        if (!res.ok) return text(`llms.txt unavailable (HTTP ${res.status}).`);
        return text(await res.text());
      },
    },
    {
      name: 'open_page',
      description: "Navigate the current browser tab to a connect0.ai page (site-relative path like '/pricing').",
      inputSchema: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Site-relative path starting with /' },
        },
        required: ['path'],
      },
      execute: async (input) => {
        if (!isSafeSitePath(input.path)) {
          return text('Refused: path must be site-relative (start with a single /).');
        }
        window.location.assign(input.path);
        return text(`Navigating to ${input.path}.`);
      },
    },
  ];
}
